import type { ExpRateChartSnapshot } from '@/domain/types'

interface ChartPosition { x: number; y: number }

/** Continuous tangents, bounded to keep interpolated rates within each pair of observations. */
function smoothPath(points: ChartPosition[]): string {
  const slopes = points.slice(1).map((p, i) => (p.y - points[i]!.y) / (p.x - points[i]!.x))
  const tangents = points.map((_, i) => {
    if (i === 0) return slopes[0]!
    if (i === points.length - 1) return slopes[i - 1]!
    const left = slopes[i - 1]!, right = slopes[i]!
    return left * right <= 0 ? 0 : Math.sign(left) * Math.min(Math.abs(left), Math.abs(right))
  })
  const coordinate = (x: number, y: number) => `${x.toFixed(2)},${y.toFixed(2)}`
  let path = `M${coordinate(points[0]!.x, points[0]!.y)}`
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!, b = points[i]!, third = (b.x - a.x) / 3
    path += ` C${coordinate(a.x + third, a.y + tangents[i - 1]! * third)} ${coordinate(b.x - third, b.y - tangents[i]! * third)} ${coordinate(b.x, b.y)}`
  }
  return path
}

export function presentExpRateChart(model?: ExpRateChartSnapshot) {
  const points = model?.points.filter(p => p.at >= model.from && p.at <= model.to) ?? []
  const values = points.flatMap(p => p.value !== null && Number.isFinite(p.value) ? [p.value] : [])
  const min = values.length ? Math.min(...values) : 0
  const max = values.length ? Math.max(...values) : 1
  const padding = min === max ? Math.max(Math.abs(min) * .05, 1) : 0
  const low = min - padding, high = max + padding
  const x = (at: number) => 3 + 314 * Math.max(0, Math.min(1, (at - (model?.from ?? 0)) / Math.max(1, (model?.to ?? 1) - (model?.from ?? 0))))
  const y = (value: number) => 59 - (value - low) / (high - low) * 54
  const groups: { line: string; area: string }[] = []
  let group: ChartPosition[] = [], previousSegment = -1, previousAt = -Infinity
  const flush = () => {
    if (group.length > 1) {
      const line = smoothPath(group)
      groups.push({ line, area: `${line} L${group.at(-1)!.x},64 L${group[0]!.x},64 Z` })
    }
    group = []
  }
  const dots: { at: number; value: number; x: number; y: number }[] = []
  for (const p of points) {
    if (p.value === null || !Number.isFinite(p.value)) { flush(); continue }
    if (p.segment !== previousSegment || model?.gaps.some(g => g.from < p.at && g.to > previousAt)) flush()
    group.push({ x: x(p.at), y: y(p.value) })
    dots.push({ at: p.at, value: p.value, x: x(p.at), y: y(p.value) })
    previousSegment = p.segment; previousAt = p.at
  }
  flush()
  const average = values.length && model?.average != null && Number.isFinite(model.average)
    ? { y: Math.max(5, Math.min(59, y(model.average))), edge: model.average > high ? 'higher' as const : model.average < low ? 'lower' as const : null, value: model.average } : null
  return { groups, dots, min, max, average, x, gaps: (model?.gaps ?? []).map(g => ({ ...g, x: x(g.from), width: Math.max(1, x(g.to) - x(g.from)) })) }
}
